import { useState, useEffect } from 'react';
import LandingPage from '../components/LandingPage';
import { detectLocale } from '../locales/config';

const faqItems = [
  {
    q: "How does the debt calculator work for India?",
    a: "Enter each debt with the outstanding balance, annual interest rate, and minimum monthly payment. The debt repayment calculator India applies the snowball or avalanche method to show your exact debt-free date, total interest paid, and how much you save by paying a little extra each month — whether for credit cards, EMI loans, or personal loans.",
  },
  {
    q: "Can I use this for EMI loans and credit cards?",
    a: "Yes. This EMI debt calculator works for any debt with interest — credit cards, personal loans, home loans, car loans, education loans, and buy-now-pay-later EMIs. Enter multiple debts at once to see your complete debt repayment plan and calculate when you will be debt free in India.",
  },
  {
    q: "What is the debt snowball method?",
    a: "The debt snowball method India means paying the minimum on all debts and putting every extra rupee toward your smallest debt first. Once it's cleared, you roll that payment into the next smallest. Each debt you eliminate frees up more cash to attack the next one faster. The avalanche method targets the highest interest rate first and saves more in total interest.",
  },
  {
    q: "How long will it take to become debt free in India?",
    a: "It depends on your balance, interest rate, and monthly payment. A credit card debt of ₹1,00,000 at 36% per year paying only the minimum can take over 10 years to clear. Adding ₹2,000–₹5,000 extra per month can cut that to 2–3 years and save tens of thousands in interest. Use the debt calculator India to see your personalised plan instantly.",
  },
  {
    q: "Is this calculator free to use?",
    a: "The debt calculator is completely free — no signup, no subscription. Enter your debts and get your personalised debt repayment plan in seconds. If you want to save and download your plan as a PDF, there is a one-time payment option with no recurring charges.",
  },
];

const COLORS = { text: "#e8e8f0", muted: "#9999b5", accent: "#00e5a0" };

const sections = [
  {
    heading: 'How to use this debt repayment calculator India',
    body: 'Enter each debt — credit card, personal loan, EMI, or home loan — with its outstanding balance, annual interest rate, and minimum monthly payment. Add any extra amount you can put toward repayment. The debt calculator India shows your debt-free date, total interest, and exactly how each extra rupee accelerates your journey to become debt free in India.',
  },
  {
    heading: 'Debt snowball vs avalanche — which is better for India?',
    body: (
      <>
        <p style={{ color: COLORS.muted, lineHeight: 1.7, marginBottom: 12 }}>
          The <strong style={{ color: COLORS.text }}>debt snowball method India</strong> pays off
          your smallest debt first, then rolls that freed-up EMI into the next. Quick wins build
          momentum and motivation — great if you have several small credit card bills or personal
          loan instalments.
        </p>
        <p style={{ color: COLORS.muted, lineHeight: 1.7, marginBottom: 0 }}>
          The <strong style={{ color: COLORS.text }}>avalanche method</strong> targets the highest
          interest rate first — typically your credit card debt — and mathematically saves you the
          most money. Use this personal loan payoff calculator India to compare both methods and
          choose the plan that fits your situation.
        </p>
      </>
    ),
  },
  {
    heading: 'Why minimum EMI payments keep you in debt longer',
    body: 'Credit card interest in India typically runs at 36–42% per year. Paying only the minimum on a ₹1,00,000 balance can trap you in debt for a decade and cost double in interest. Even adding ₹1,000–₹2,000 per month above the minimum can cut years off your timeline. Use this credit card bill calculator India to see the exact impact of every extra payment.',
  },
];

export default function IndiaDebtCalculator() {
  const [locale, setLocale] = useState('en-in');

  useEffect(() => {
    detectLocale().then(detected => {
      setLocale(detected === 'en-in' ? 'en-in' : 'en-in');
    });
  }, []);

  return (
    <LandingPage
      locale={locale}
      pageUrl="https://clearmydebt.app/in/"
      helmet={{
        lang: 'en',
        title: 'Free Debt Calculator India — Pay Off Debt Fast | ClearMyDebt',
        description: "Calculate when you'll be debt free in India. Snowball and avalanche method. No signup. Instant results. Works for EMI, credit cards and personal loans.",
        ogTitle: 'Free Debt Calculator India — Pay Off Debt Fast | ClearMyDebt',
        ogDescription: "Calculate when you'll be debt free in India. No signup. Instant results. Works for EMI, credit cards and personal loans.",
      }}
      hreflangList={[
        { hreflang: 'en-in', href: 'https://clearmydebt.app/in/' },
        { hreflang: 'x-default', href: 'https://clearmydebt.app/in/' },
      ]}
      h1="Free Debt Calculator India"
      intro="Enter your debts and find out exactly when you will be debt free in India — and how much you save with the right repayment plan."
      sections={sections}
      faqItems={faqItems}
      faqHeading="Frequently Asked Questions"
      moreHeading="More Calculators"
      moreLinks={[
        { href: '/za/', label: 'Debt Calculator South Africa' },
        { href: '/en-in/credit-card-payoff-calculator/', label: 'Credit Card Payoff' },
        { href: '/en-in/debt-snowball-calculator/', label: 'Debt Snowball' },
        { href: '/en-in/debt-avalanche-calculator/', label: 'Debt Avalanche' },
      ]}
    />
  );
}
